<template>
  <v-app>
    <!-- PWA Offline Indicator -->
    <PwaOfflineIndicator />

    <!-- Main Application Content -->
    <router-view></router-view>

    <!-- PWA Installation Prompt (iOS) -->
    <PwaInstallPrompt />

    <!-- Feature Renderer (global features system) -->
    <FeatureRenderer />

    <!-- Toast Notifications -->
    <Toaster />

    <!-- Global Invoice Cancellation Failure Banner -->
    <PersistentBanner
      v-if="cancellationStore.bannerComprobanteFailed"
      :message="bannerMessage"
      actionLabel="Ver factura"
      :actionRoute="bannerActionRoute"
      @dismiss="dismissBanner"
    />
  </v-app>
</template>

<script setup lang="ts">
  import { FeatureRenderer, useGlobalFeatures } from '@/app/features';
  import PwaInstallPrompt from '@/app/pwa/components/PwaInstallPrompt.vue';
  import PwaOfflineIndicator from '@/app/pwa/components/PwaOfflineIndicator.vue';
  import { useGlobalPwaUpdate } from '@/app/pwa/composables/usePwaUpdate';
  import { computed, watch } from 'vue';
  import { Toaster } from 'vue-sonner';
  import PersistentBanner from '@/common/components/PersistentBanner.vue';
  import { useCancellationPendingStore } from '@/modules/facturacion/stores/useCancellationPendingStore';
  import { FacturaType } from '@/modules/facturacion/types/facturaType.enum';
  import { useCancellationEffects } from '@/modules/facturacion/composables/useCancellationEffects';

  // Initialize global features system (watches auth state)
  useGlobalFeatures();

  // Initialize global background invoice cancellation effects
  useCancellationEffects();

  /**
   * @author Omar Sánchez Martínez
   * @since 2025-11-19
   * @description Main Application Component
   *
   * Integrates:
   * - PwaOfflineIndicator: Shows network status banner
   * - PwaInstallPrompt: Shows iOS installation instructions
   * - usePwaUpdate: Handles service worker updates
   * - FeatureRenderer: Renders feature modals/tooltips/banners
   * - useGlobalFeatures: Fetches pending features after login
   */
  const cancellationStore = useCancellationPendingStore();
  const { needRefresh, showUpdatePrompt } = useGlobalPwaUpdate();

  /**
   * Banner content for cancellation failure
   */
  const bannerMessage = computed(() => {
    const comprobante = cancellationStore.bannerComprobanteFailed;
    if (!comprobante) return '';
    const folioStr = comprobante.folio ? `-${comprobante.folio}` : '';
    return `No se pudo completar la cancelación de la factura <strong>${comprobante.serie}${folioStr}</strong> de <strong>${comprobante.receptor_razon_social}</strong>, por favor intente cancelar nuevamente.`;
  });

  const bannerActionRoute = computed(() => {
    const comprobante = cancellationStore.bannerComprobanteFailed;
    if (!comprobante) return { name: 'home' };

    // Determine route based on invoice type
    if (comprobante.comprobante_type === FacturaType.GLOBAL) {
      return {
        name: 'facturacion.facturas-globales.detail',
        params: { facturaGlobalId: comprobante.id },
      };
    }

    if (comprobante.comprobante_type === FacturaType.PAGO && comprobante.parent_comprobante_id) {
      return {
        name: 'facturacion.facturas.detail',
        params: { facturaId: comprobante.parent_comprobante_id },
      };
    }

    return {
      name: 'facturacion.facturas.detail',
      params: { facturaId: comprobante.id },
    };
  });

  const dismissBanner = () => {
    cancellationStore.bannerComprobanteFailed = null;
  };

  /**
   * Watch for service worker updates
   * When a new version is available, show the update prompt
   */
  watch(needRefresh, refresh => {
    if (refresh) {
      showUpdatePrompt();
    }
  });
</script>
